import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ApplicationModalPropsType, Avatar } from '.';
import { ElementForAttachment } from '../attachments';
import {
    Activity,
    Attachment,
    Entity,
    FeedbackEntity,
    TextStyleEntity,
} from '../types';
import styles from './BotMessage.module.css';
import sharedStyles from '../shared.module.css';
import { InputMode } from '../hooks';
import {
    addAttributionToAnswer,
    getTenantName,
    isCookieEnabled,
    isLatestRehydratedMessage,
} from '../helpers';
import { AmpFlagSet } from '../amplitude';
import { logEvent as logAnalyticsEvent } from '../analytics';
import { GraceFeatureFlags } from '../featureFlags';
import { getConversationId, logEvent } from '../logging';
import { MessageRenderer } from '../interfaces/messageRenderer';
import { GraceChatIcon } from '../icons';
import { TENANTS } from '../constants';
import {
    FeedbackQnA,
    FeedbackSelectedStatus,
} from '../attachments/FeedbackQnA';
import { getCorrelationId } from '../logging';
import { ReactComponent as BotInfoSvg } from '../icons/bot_info.svg';
import { BotInfoDialog } from './BotInfoDialog';

const expandWidthOfBotMessage = (
    activity: Activity,
    disableButtons: boolean
) => {
    if (activity.attachments) {
        // Button lists hide buttons after selection, do not expand width
        if (activity.attachments.length === 1) {
            const contentType = activity.attachments[0].contentType;
            const isButtonList =
                contentType === 'application/providence.buttonlist' ||
                contentType === 'application/vnd.microsoft.card.adaptive';
            if (isButtonList && disableButtons) {
                return false;
            }
        }

        // Default case if we have attachments, assume we should give full width
        return true;
    }

    return false;
};

export type BotMessageProps = {
    activity: Activity;
    disableButtons: boolean;
    displayBotName: boolean;
    featureFlags: AmpFlagSet;
    idx: number;
    appendMessage?: (message: Activity) => void;
    sendMessage: (message: string) => void;
    sendPostBack: (message: string) => void;
    sendEvent: (name: string, value: any) => void;
    setInputMode: (inputMode: InputMode) => void;
    setModalProperties?: (modalProperties: ApplicationModalPropsType) => void;
    setFeedbackStatus?: (status: FeedbackSelectedStatus) => void;
    messageRenderer: MessageRenderer;
};

export const BotMessage: React.FC<BotMessageProps> = ({
    activity,
    disableButtons,
    displayBotName,
    featureFlags,
    idx,
    appendMessage,
    sendMessage,
    sendPostBack,
    sendEvent,
    setInputMode,
    setModalProperties,
    setFeedbackStatus,
    messageRenderer,
}) => {
    const firstRender = useRef(true);
    const tenantName = getTenantName();
    const [displayBotInfo, setDisplayBotInfo] = useState(false);

    const feedbackEntity =
        activity.entities &&
        (activity.entities.filter(
            (entity: Entity) => 'feedback' in entity && entity.feedback
        )[0] as FeedbackEntity);

    const showFeedbackDialogFlow =
        feedbackEntity &&
        (feedbackEntity as FeedbackEntity).feedbackResponseType
            ? featureFlags[GraceFeatureFlags.Feedback] || false
            : true;

    // TODO - update AttachmentType to include removeBubble where appropriate
    const removeBubble =
        activity.attachments &&
        activity.attachments.length &&
        (activity.attachments[0].content as any).removeBubble;

    const botMessageClass = removeBubble
        ? sharedStyles.botMessageNoBubble
        : sharedStyles.botMessageBubble;

    const botMessageClassStyle: React.CSSProperties = {};
    const hideAvatar = false;
    if (expandWidthOfBotMessage(activity, disableButtons)) {
        botMessageClassStyle.flexGrow = 1;
    }
    const botMessageContainerClass = hideAvatar
        ? sharedStyles.botMessageContainerNoAvatar
        : sharedStyles.botMessageContainer;
    const botMessageTextClass =
        tenantName === TENANTS.MESSAGE_ASSIST
            ? sharedStyles.botMessageText
            : sharedStyles.botMessageTextNoIcon;

    const textStyleEntity = activity.entities?.find(
        (entity: Entity) => 'name' in entity && entity.name === 'textStyle'
    ) as TextStyleEntity;

    const textStyle = textStyleEntity && textStyleEntity.value;

    let mostRecentMessage = false;
    try {
        if (
            featureFlags[GraceFeatureFlags.DirectLineReconnect] &&
            (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal] ||
                isCookieEnabled())
        ) {
            const watermark = activity.id.split('|')[1];
            mostRecentMessage = isLatestRehydratedMessage(
                featureFlags,
                watermark
            );
        } else {
            mostRecentMessage = !disableButtons;
        }
    } catch (_er) {
        // noop
    }

    const { text, kbIntent, kbSource, redactionDelimiter } = activity;

    // Redact sensitive information
    const willRedact = !!(text && redactionDelimiter);
    const redactedText = willRedact
        ? text.replaceAll(
              new RegExp(
                  `${redactionDelimiter}[\\s\\S]*?${redactionDelimiter}`,
                  'g'
              ),
              '**[REDACTED]**'
          )
        : text || '';

    let textWithoutDelimiters = text || '';

    if (redactionDelimiter && redactionDelimiter.length > 0) {
        textWithoutDelimiters = textWithoutDelimiters.replaceAll(
            redactionDelimiter,
            ''
        );
    }

    const textWithAttribution = addAttributionToAnswer(
        textWithoutDelimiters,
        kbIntent,
        kbSource
    );

    useEffect(() => {
        if (!text) {
            return;
        }

        // Log AppInsights event on first render of component
        if (firstRender.current) {
            firstRender.current = false;
            logEvent({
                name: 'BotMessageDisplayed',
                properties: {
                    text: messageRenderer.render(redactedText),
                    redacted: willRedact,
                },
            });
        }
    }, [text]);

    const handleBotNameClick = () => {
        logAnalyticsEvent('Bot Info - Opened', {
            correlation_id: getCorrelationId(),
            conversation_id: getConversationId(),
        });
        setDisplayBotInfo(true);
    };

    const botNameChildren = (
        <div
            className={classNames(botMessageClass, styles.botMessageNameLabel)}
        >
            {tenantName === TENANTS.MESSAGE_ASSIST && (
                <GraceChatIcon fontSize="1.5rem" />
            )}
            <span className={botMessageTextClass}>
                Grace (AI virtual assistant)
            </span>
            {tenantName === TENANTS.MESSAGE_ASSIST && <BotInfoSvg />}
        </div>
    );
    let botNameElement = (
        <div key={`bot-name-${idx}`} className={botMessageContainerClass}>
            {botNameChildren}
        </div>
    );
    if (tenantName === TENANTS.MESSAGE_ASSIST) {
        botNameElement = (
            <button
                key={`bot-name-${idx}`}
                className={classNames(
                    botMessageContainerClass,
                    sharedStyles.botNameButton
                )}
                onClick={handleBotNameClick}
            >
                {botNameChildren}
            </button>
        );
    }

    return textWithoutDelimiters || activity.attachments ? (
        <div className={sharedStyles.botMessageRowContainer} key={`row-${idx}`}>
            <BotInfoDialog
                key={'botInfoDialog'}
                isOpen={displayBotInfo}
                setIsOpen={setDisplayBotInfo}
            />
            {displayBotName && botNameElement}
            <div key={`transcript-${idx}`} className={botMessageContainerClass}>
                {!hideAvatar && (
                    <div className={sharedStyles.botMessageAvatar}>
                        <Avatar />
                    </div>
                )}
                <div className={botMessageClass} style={botMessageClassStyle}>
                    {textWithoutDelimiters ? (
                        <div
                            data-bot-msg="true"
                            data-kb-intent={kbIntent}
                            data-kb-source={kbSource}
                            dangerouslySetInnerHTML={{
                                __html: messageRenderer.render(
                                    textWithAttribution
                                ),
                            }}
                            key={`markdown-${idx}`}
                            style={textStyle}
                        />
                    ) : null}
                    {activity.attachments ? (
                        <div>
                            {activity.attachments.map(
                                (
                                    attachment: Attachment,
                                    attachmentIdx: number
                                ) => (
                                    <ElementForAttachment
                                        attachment={attachment}
                                        disableButtons={disableButtons}
                                        featureFlags={featureFlags}
                                        key={`attachment-${idx}-${attachmentIdx}`}
                                        appendMessage={appendMessage}
                                        sendMessage={sendMessage}
                                        sendPostBack={sendPostBack}
                                        sendEvent={sendEvent}
                                        setInputMode={setInputMode}
                                        setModalProperties={setModalProperties}
                                        mostRecentMessage={mostRecentMessage}
                                    />
                                )
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
            {feedbackEntity &&
            feedbackEntity.feedback &&
            showFeedbackDialogFlow ? (
                <div className={sharedStyles.botMessageFeedback}>
                    <FeedbackQnA
                        initialSelectedFeedback={FeedbackSelectedStatus.None}
                        source={feedbackEntity.source || ''}
                        intent={feedbackEntity.intent || ''}
                        correlationId={getCorrelationId()}
                        setFeedbackStatus={setFeedbackStatus}
                    />
                </div>
            ) : null}
        </div>
    ) : null;
};
